/**
 * Recording and sending a voicemail.
 *
 * The bytes go straight from the microphone to object storage with a presigned
 * PUT — the same three-step upload chat voice messages use — and only the file
 * id reaches apps/api. That is what makes this a real recording rather than the
 * empty inbox the Voicemail tab used to be.
 */
import { useCallback, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from '@cyphex/ui';
import { uploadFile } from '@/features/chat/lib/uploadFile';
import type { VoiceClipResult } from '@/features/chat/hooks/useVoiceRecorder';
import { callsApi, type VoicemailEligibility } from '../data/callsApi';
import { CALL_KEYS } from '../data/queryKeys';

/** Whether the "Leave a message" control may be shown at all. */
export function useVoicemailEligibility(
  callId: string,
  enabled: boolean,
): VoicemailEligibility | undefined {
  return useQuery({
    queryKey: [...CALL_KEYS.all, 'voicemail-eligibility', callId],
    queryFn: () => callsApi.voicemailEligibility(callId),
    enabled: enabled && callId !== '',
    retry: false,
  }).data;
}

export interface LeaveVoicemailResult {
  send: (clip: VoiceClipResult) => void;
  isSending: boolean;
  isSent: boolean;
}

export function useLeaveVoicemail(callId: string): LeaveVoicemailResult {
  const queryClient = useQueryClient();
  const [isSent, setSent] = useState(false);

  const mutation = useMutation({
    mutationFn: async (clip: VoiceClipResult) => {
      const file = new File([clip.blob], `voicemail-${Date.now()}.webm`, {
        type: clip.blob.type || 'audio/webm',
      });
      /* SCOPED TO THE CALL. The file-access gate hands a call's files to that
         call's participants, so scoping it here is what lets the RECIPIENT
         play it back — a recording left in the sender's own drawer is a
         recording nobody else can hear. */
      const { fileId } = await uploadFile(file, { kind: 'call', id: callId });
      return callsApi.leaveVoicemail(callId, {
        fileId,
        durationMs: clip.durationMs,
        waveform: clip.waveform,
      });
    },
    onSuccess: () => {
      setSent(true);
      void queryClient.invalidateQueries({ queryKey: CALL_KEYS.voicemail() });
      toast.success('Message sent.');
    },
    onError: () => toast.error('Could not send that message. Try again.'),
  });

  const send = useCallback(
    (clip: VoiceClipResult) => {
      mutation.mutate(clip);
    },
    [mutation],
  );

  return { send, isSending: mutation.isPending, isSent };
}
